import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Truck, CalendarRange, Package, User, MapPin, CheckCircle, FileText, Camera, AlertCircle, Droplet, PanelTop, X } from 'lucide-react';
import { getShipmentByReference, createAuditoriaCalidad, uploadAuditoriaImage, getAuditoriasByShipmentId, api, getShipmentLoadingPhaseInfo, updateShipmentStatus, updateOrdersStatusByShipment } from '../api';
import AuditoriaPDF from '../components/AuditoriaPDF';

const ConfirmarEmbarque = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false); // Por defecto cerrado en móviles
  const { userPermissions } = useAuth();  const [loading, setLoading] = useState(true);
  const [embarque, setEmbarque] = useState(null);
  const [auditorias, setAuditorias] = useState([]);
  const [activeItem, setActiveItem] = useState('Auditorías de Calidad');
  const [modoAuditoria, setModoAuditoria] = useState(false);
  const [etapaAuditoria, setEtapaAuditoria] = useState(1);
  const [formAuditoria, setFormAuditoria] = useState({
    // Sección Fauna Nociva
    faunaNociva: false,
    fotosFaunaNociva: [],
    
    // Sección Filtraciones
    filtracionesLuzAgua: false,
    fotosFiltraciones: [],
    
    
    // Sección Estado Físico
    estadoFisico: 'Bueno', // 'Bueno', 'Regular', 'Malo'
    fotosEstadoFisico: [],
    
    // Sección Comentarios y Decisión Final
    comentarios: '',
    autorizado: null // true = autorizado, false = rechazado, null = no decidido
  });
  const navigate = useNavigate();
  const location = useLocation();
  
  // Obtener el ID del embarque de los parámetros de la URL o del estado enviado
  const [embarqueId, setEmbarqueId] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  
  const handleLogout = () => {
    console.log('Logout clicked!');
    localStorage.clear();
    setTimeout(() => {
      navigate('/login');
    }, 0);
  };  // Función para buscar información del embarque
  const buscarEmbarque = async () => {
    if (!embarqueId.trim()) {
      setErrorMsg('Por favor ingresa un ID de embarque válido');
      return;
    }
    
    setLoading(true);
    setErrorMsg('');
    setEmbarque(null); // Resetear el embarque para evitar mostrar datos viejos
    
    try {
      // Mostrar en consola la referencia que estamos buscando para depuración
      console.log('Buscando embarque con referencia:', embarqueId);
      
      // Llamada a la API para obtener datos reales del embarque
      const shipment = await getShipmentByReference(embarqueId);
        if (shipment) {
        console.log('Embarque encontrado:', shipment);
        
        // Obtener auditorías asociadas al embarque
        try {
          const auditoriasData = await getAuditoriasByShipmentId(shipment.id);
          console.log('Auditorías encontradas:', auditoriasData);
          setAuditorias(auditoriasData || []);
        } catch (errorAudit) {
          console.error('Error al obtener auditorías:', errorAudit);
          setAuditorias([]);
        }
        
        // Formatear los datos del embarque para mostrarlos en la interfaz
        const formattedShipment = {
          id: shipment.id,
          numeroReferencia: shipment.reference,
          estado: shipment.status,
          substatus: shipment.substatus || shipment.Substatus, // <-- Añadido para robustez
          fechaSalida: shipment.pickup_date,
          fechaEstimadaLlegada: shipment.delivery_date,
          origen: shipment.origin || 'No especificado',
          destino: shipment.destination || 'No especificado',
          transportista: shipment.Lt || 'No especificado',
          conductor: shipment.driver || 'No especificado',
          vehiculo: shipment.vehicle || 'No especificado',
          truck: shipment.truck || shipment.Truck || 'No especificado',
          remolque1: shipment.remolque1 || 'No especificado',
          cliente: shipment.customer || 'No especificado',
          carga: [],
          lastUpdated: shipment.updated_at
        };
        
        // Si el embarque tiene órdenes, extraer información de la carga
        if (shipment.orders && shipment.orders.length > 0) {
          // Agrupar productos por tipo para mostrarlos en la carga
          const groupedProducts = {};
          
          shipment.orders.forEach(order => {
            if (order.productos && order.productos.length > 0) {
              order.productos.forEach(prod => {
                const key = prod.descripcion || 'Productos sin descripción';
                if (!groupedProducts[key]) {
                  groupedProducts[key] = {
                    descripcion: key,
                    cantidad: 0,
                    unidad: prod.unidad || 'unidades'
                  };
                }
                groupedProducts[key].cantidad += prod.cantidad || 1;
              });
            } else {
              // Si no hay productos detallados, usar la orden como elemento de carga
              const key = `Orden ${order.Order_number}`;
              groupedProducts[key] = {
                descripcion: key,
                cantidad: 1,
                unidad: 'orden'
              };
            }
          });
          
          formattedShipment.carga = Object.values(groupedProducts);
        }
        
        setEmbarque(formattedShipment);
      } else {
        console.warn('No se encontró ningún embarque con la referencia:', embarqueId);
        setErrorMsg('No se encontró ningún embarque con esa referencia');
      }
    } catch (error) {
      console.error('Error al buscar el embarque:', error);
      setErrorMsg(`Error al buscar la información del embarque: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };  // Si recibimos un código QR de la página anterior, lo usamos para buscar automáticamente
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const qrCode = params.get('qr');
    
    if (qrCode) {
      const embarqueRef = qrCode.trim();
      console.log("QR recibido, buscando embarque con referencia:", embarqueRef);
      
      // Hacer una búsqueda directa con la referencia del QR sin actualizar el estado
      const buscarEmbarquePorQR = async () => {
        if (!embarqueRef) {
          setErrorMsg('Código QR inválido');
          setLoading(false);
          return;
        }
        
        setLoading(true);
        setErrorMsg('');
        setEmbarque(null); // Resetear el embarque para evitar mostrar datos viejos
        
        try {
          console.log('Buscando embarque directamente con referencia del QR:', embarqueRef);
          
          // Llamada a la API para obtener datos reales del embarque
          const shipment = await getShipmentByReference(embarqueRef);
          
          // Actualizar el input después de la búsqueda
          setEmbarqueId(embarqueRef);
          
          if (shipment) {
            console.log('Embarque encontrado por QR:', shipment);
            
            // Obtener auditorías asociadas al embarque
            try {
              const auditoriasData = await getAuditoriasByShipmentId(shipment.id);
              console.log('Auditorías encontradas por QR:', auditoriasData);
              setAuditorias(auditoriasData || []);
            } catch (errorAudit) {
              console.error('Error al obtener auditorías por QR:', errorAudit);
              setAuditorias([]);
            }
            
            // Formatear los datos del embarque para mostrarlos en la interfaz
            const formattedShipment = {
              id: shipment.id,
              numeroReferencia: shipment.reference,
              estado: shipment.status,
              substatus: shipment.substatus || shipment.Substatus, // <-- Añadido para robustez
              fechaSalida: shipment.pickup_date,
              fechaEstimadaLlegada: shipment.delivery_date,
              origen: shipment.origin || 'No especificado',
              destino: shipment.destination || 'No especificado',
              transportista: shipment.Lt || 'No especificado',
              conductor: shipment.driver || 'No especificado',
              vehiculo: shipment.vehicle || 'No especificado',
              truck: shipment.truck || shipment.Truck || 'No especificado',
              remolque1: shipment.remolque1 || 'No especificado',
              cliente: shipment.customer || 'No especificado',
              carga: [],
              lastUpdated: shipment.updated_at
            };
            
            // Si el embarque tiene órdenes, extraer información de la carga
            if (shipment.orders && shipment.orders.length > 0) {
              // Agrupar productos por tipo para mostrarlos en la carga
              const groupedProducts = {};
              
              shipment.orders.forEach(order => {
                if (order.productos && order.productos.length > 0) {
                  order.productos.forEach(prod => {
                    const key = prod.descripcion || 'Productos sin descripción';
                    if (!groupedProducts[key]) {
                      groupedProducts[key] = {
                        descripcion: key,
                        cantidad: 0,
                        unidad: prod.unidad || 'unidades'
                      };
                    }
                    groupedProducts[key].cantidad += prod.cantidad || 1;
                  });
                } else {
                  // Si no hay productos detallados, usar la orden como elemento de carga
                  const key = `Orden ${order.Order_number}`;
                  groupedProducts[key] = {
                    descripcion: key,
                    cantidad: 1,
                    unidad: 'orden'
                  };
                }
              });
              
              formattedShipment.carga = Object.values(groupedProducts);
            }
            
            setEmbarque(formattedShipment);
          } else {
            console.warn('No se encontró ningún embarque con la referencia del QR:', embarqueRef);
            setErrorMsg('No se encontró ningún embarque con la referencia escaneada');
          }
        } catch (error) {
          console.error('Error al buscar el embarque por QR:', error);
          setErrorMsg(`Error al buscar la información del embarque: ${error.message}`);
        } finally {
          setLoading(false);
        }
      };
      
      // Ejecutar la búsqueda directamente
      buscarEmbarquePorQR();
    } else {
      setLoading(false); // Asegurarse de que el loading se detenga si no hay QR
    }
    
    document.title = "Confirmar Embarque | Amerisa";
  }, [location.search]);
  // Formatear una fecha para mostrarla
  const formatearFecha = (fechaStr) => {
    if (!fechaStr) return 'N/A';
    
    try {
      // Manejar diferentes formatos de fecha que pueden venir del backend
      const fecha = new Date(fechaStr);
      
      // Verificar si la fecha es válida
      if (isNaN(fecha.getTime())) {
        return 'Fecha no válida';
      }
      
      return fecha.toLocaleString('es-MX', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      console.error('Error al formatear fecha:', error);
      return 'Error en formato de fecha';
    }
  };
  // Función para iniciar el proceso de auditoría
  const iniciarAuditoria = () => {
    setModoAuditoria(true);
    setEtapaAuditoria(1); // Iniciar en la primera etapa
  };

  // Función para avanzar a la siguiente etapa de la auditoría
  const avanzarEtapa = () => {
    setEtapaAuditoria(prevEtapa => prevEtapa + 1);
  };

  // Función para retroceder a la etapa anterior
  const retrocederEtapa = () => {
    setEtapaAuditoria(prevEtapa => Math.max(1, prevEtapa - 1));
  };

  // Función para cancelar la auditoría
  const cancelarAuditoria = () => {
    if (window.confirm("¿Estás seguro de que deseas cancelar esta auditoría? Los datos no se guardarán.")) {
      setModoAuditoria(false);
      setEtapaAuditoria(1);
      // Reiniciar el formulario de auditoría
      setFormAuditoria({
        faunaNociva: false,
        fotosFaunaNociva: [],
        filtracionesLuzAgua: false,
        fotosFiltraciones: [],
        estadoFisico: 'Bueno',
        fotosEstadoFisico: [],
        comentarios: '',
        autorizado: null
      });
    }
  };

  // Función para manejar cambios en el formulario de auditoría
  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormAuditoria({
      ...formAuditoria,
      [name]: type === 'checkbox' ? checked : value
    });
  };  // Función para manejar la subida de imágenes
  const handleImageUpload = (e, seccion) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    // Determinamos qué array de fotos actualizar y el tipo de evidencia según la sección
    let fotosKey;
    let tipoEvidencia;
    switch(seccion) {
      case 'faunaNociva':
        fotosKey = 'fotosFaunaNociva';
        tipoEvidencia = 'fauna';
        break;
      case 'filtraciones':
        fotosKey = 'fotosFiltraciones';
        tipoEvidencia = 'filtracion';
        break;
      case 'estadoFisico':
        fotosKey = 'fotosEstadoFisico';
        tipoEvidencia = 'estado';
        break;
      default:
        console.error('Sección de fotos no válida');
        return;
    }
    
    // Almacenamos las imágenes localmente - ya no intentamos subirlas inmediatamente
    const localFiles = files.map(f => ({
      nombre: f.name,
      url: URL.createObjectURL(f),
      file: f,
      tipoEvidencia: tipoEvidencia, // Guardamos el tipo de evidencia para usarlo al subir más tarde
      pendienteSubida: true // Marcamos que está pendiente de subir al servidor
    }));
    
    setFormAuditoria(prev => ({
      ...prev,
      [fotosKey]: [...prev[fotosKey], ...localFiles]
    }));
    
    console.log(`${files.length} imágenes agregadas a la sección ${seccion} - se subirán al finalizar la auditoría`);
  };

  // Función para eliminar una foto de evidencia
  const eliminarFoto = (seccion, index) => {
    // Determinamos qué array de fotos actualizar según la sección
    let fotosKey;
    switch(seccion) {
      case 'faunaNociva':
        fotosKey = 'fotosFaunaNociva';
        break;
      case 'filtraciones':
        fotosKey = 'fotosFiltraciones';
        break;
      case 'estadoFisico':
        fotosKey = 'fotosEstadoFisico';
        break;
      default:
        console.error('Sección de fotos no válida');
        return;
    }
    
    const nuevosFotos = [...formAuditoria[fotosKey]];
    // Liberar la URL del objeto si fue creada localmente
    if (nuevosFotos[index].url && nuevosFotos[index].pendienteSubida) {
      URL.revokeObjectURL(nuevosFotos[index].url);
    }
    nuevosFotos.splice(index, 1);
    
    setFormAuditoria({
      ...formAuditoria,
      [fotosKey]: nuevosFotos
    });
  };  // Función para enviar la auditoría completa
  const enviarAuditoria = async () => {
    setLoading(true);
    try {
      // Verificar que se haya tomado una decisión (autorizar o rechazar)
      if (formAuditoria.autorizado === null) {
        throw new Error('Debe seleccionar si autoriza o rechaza el embarque');
      }
        // Preparar los datos de la auditoría para enviar según el esquema del backend
      const auditoriaData = {
        // Información del embarque si existe
        shipment_id: embarque?.id || null,
        
        // Si no hay embarque vinculado, incluir la información básica
        camion: embarque?.truck || embarque?.Truck || null, // Usamos truck del embarque en lugar de vehículo
        operador: embarque?.conductor || null,
        tractor: embarque?.vehiculo || null, // Usamos las placas del vehículo
        remolque: embarque?.remolque1 || null, // Usamos las placas del remolque
        lt: embarque?.transportista || null,
        destino: embarque?.destino || null,
        
        // Aspectos evaluados - inicialmente sin imágenes
        fauna_nociva: formAuditoria.faunaNociva,
        fauna_img: [],
        
        filtracion: formAuditoria.filtracionesLuzAgua,
        filtracion_img: [],
        
        estado_fisico: formAuditoria.estadoFisico.toUpperCase(),
        estado_img: [],
        
        // Decisión y comentarios
        comentarios: formAuditoria.comentarios,
        estatus: formAuditoria.autorizado ? 'AUTORIZADO' : 'RECHAZADO'
      };
      
      console.log('Enviando datos de auditoría:', auditoriaData);
      
      // 1. PRIMERO - Enviar los datos básicos de la auditoría
      const auditoriaResponse = await createAuditoriaCalidad(auditoriaData);
      
      if (!auditoriaResponse || !auditoriaResponse.id) {
        throw new Error('Error al crear la auditoría: No se recibió ID');
      }
      
      const auditoriaId = auditoriaResponse.id;
      console.log(`Auditoría creada con ID: ${auditoriaId}`);      // 2. SEGUNDO - Ahora subir todas las imágenes a la auditoría ya creada
      // Verificamos si hay fotos pendientes de subir
      const pendingPhotos = [
        ...formAuditoria.fotosFaunaNociva.filter(f => f.file && f.pendienteSubida),
        ...formAuditoria.fotosFiltraciones.filter(f => f.file && f.pendienteSubida),
        ...formAuditoria.fotosEstadoFisico.filter(f => f.file && f.pendienteSubida)
      ];
      
      if (pendingPhotos.length > 0) {
        console.log(`Subiendo ${pendingPhotos.length} imágenes pendientes...`);
        setErrorMsg(`Subiendo ${pendingPhotos.length} imágenes... Por favor espere.`);
          try {
          // Subimos las fotos en grupos por su tipo y capturamos los IDs devueltos
          const faunaIds = await subirImagenesPorCategoria(auditoriaId, formAuditoria.fotosFaunaNociva, 'fauna');
          const filtracionIds = await subirImagenesPorCategoria(auditoriaId, formAuditoria.fotosFiltraciones, 'filtracion');
          const estadoIds = await subirImagenesPorCategoria(auditoriaId, formAuditoria.fotosEstadoFisico, 'estado');
          
          console.log("Todas las imágenes subidas correctamente");
          
          // No es necesario volver a crear la auditoría, ya que el backend habrá actualizado los campos
          // al subir las imágenes
        } catch (error) {
          console.error("Error al subir las imágenes:", error);
          setErrorMsg(`Error al subir algunas imágenes: ${error.message}`);
          // Continuamos con el proceso aunque haya errores en algunas imágenes
        }
      }
      
      // Mostrar mensaje de éxito
      console.log('Auditoría registrada exitosamente con ID:', auditoriaId);

      // LOGS DE DEPURACIÓN ANTES DE CAMBIO DE ESTATUS
      console.log('--- DEPURACIÓN CAMBIO DE ESTATUS ---');
      console.log('formAuditoria.autorizado:', formAuditoria.autorizado);
      console.log('embarque.estado:', embarque?.estado);
      console.log('embarque.status:', embarque?.status);
      console.log('embarque.substatus:', embarque?.substatus);
      console.log('embarque.Substatus:', embarque?.Substatus);
      console.log('-----------------------------------');

      // Si la auditoría es AUTORIZADA y el embarque está ARRIBADO/PENDIENTE, avanzar a CARGANDO
      if (
        formAuditoria.autorizado === true &&
        embarque &&
        (
          (embarque.estado === 'ARRIBADO' || embarque.status === 'ARRIBADO') &&
          (embarque.substatus === 'PENDIENTE' || embarque.Substatus === 'PENDIENTE')
        )
      ) {
        // Obtener órdenes completas si no están en el objeto embarque
        let embarqueCompleto = embarque;
        if (!embarque.orders) {
          try {
            // Buscar el embarque actualizado desde la API para obtener las órdenes
            const embarqueApi = await getShipmentByReference(embarque.numeroReferencia || embarque.reference);
            // El API puede devolver un array o un objeto, normalizar
            if (Array.isArray(embarqueApi) && embarqueApi.length > 0) {
              embarqueCompleto = { ...embarque, orders: embarqueApi[0].orders };
            } else if (embarqueApi && embarqueApi.orders) {
              embarqueCompleto = { ...embarque, orders: embarqueApi.orders };
            } else {
              embarqueCompleto = embarque;
            }
          } catch (e) {
            embarqueCompleto = embarque;
          }
        }
        await avanzarEmbarqueACargando(embarqueCompleto);
      }

      // Mostrar notificación de éxito
      alert(`Auditoría ${auditoriaData.estatus === 'AUTORIZADO' ? 'autorizada' : 'rechazada'} correctamente`);
      // Redireccionar
      navigate('/auditorias-calidad');
    } catch (error) {
      console.error('Error al enviar auditoría:', error);
      setErrorMsg(`Error al enviar auditoría: ${error.message || 'Ocurrió un problema en el servidor'}`);
      setLoading(false);
    }
  };

  // Función auxiliar para subir imágenes por categoría
  const subirImagenesPorCategoria = async (auditoriaId, imagenes, tipoEvidencia) => {
    const imagenesPendientes = imagenes.filter(img => img.file && img.pendienteSubida);
    
    if (imagenesPendientes.length === 0) return;
    
    console.log(`Subiendo ${imagenesPendientes.length} imágenes de tipo ${tipoEvidencia}...`);
    
    // Creamos un FormData con todas las imágenes de la misma categoría
    const formData = new FormData();
    imagenesPendientes.forEach(img => {
      formData.append('files', img.file);
    });
    
    try {      // Construimos el endpoint
      let endpoint = `/api/v1/auditorias/calidad/${auditoriaId}/upload_evidence`;
      if (tipoEvidencia) {
        endpoint += `?tipo_evidencia=${tipoEvidencia}`;
      }
      
      // Hacemos una única llamada para subir todas las imágenes de la misma categoría
      const response = await api.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });
      
      console.log(`Imágenes de tipo ${tipoEvidencia} subidas correctamente:`, response.data);
      
      // Retornar solo los IDs de las imágenes subidas
      if (response.data && response.data.files) {
        return response.data.files.map(file => file.id);
      }
      
      return response.data;
    } catch (error) {
      console.error(`Error al subir imágenes de tipo ${tipoEvidencia}:`, error);
      throw error;
    }
  };

  // Función auxiliar para avanzar el embarque a CARGANDO y actualizar órdenes (como ModalEstatus)
  const avanzarEmbarqueACargando = async (embarque) => {
    // Obtener info de fases de carga si aplica
    let loadingPhaseInfo = null;
    try {
      loadingPhaseInfo = await getShipmentLoadingPhaseInfo(embarque.id);
    } catch (e) {
      // Si falla, continuar sin info de fases
      loadingPhaseInfo = null;
    }

    // Cambiar estatus del embarque a ARRIBADO/CARGANDO
    console.log('[avanzarEmbarqueACargando] PRE updateShipmentStatus', { id: embarque.id, status: 'ARRIBADO', substatus: 'CARGANDO' });
    await updateShipmentStatus(embarque.id, 'ARRIBADO', 'CARGANDO');
    console.log('[avanzarEmbarqueACargando] POST updateShipmentStatus');

    // Actualizar órdenes asociadas (solo las del origen/fase actual si aplica)
    let orderIds = null;
    if (loadingPhaseInfo && loadingPhaseInfo.current_phase > 0 && loadingPhaseInfo.current_origin && embarque.orders) {
      orderIds = embarque.orders
        .filter(order => order.Origin === loadingPhaseInfo.current_origin && order.loading_order === loadingPhaseInfo.current_phase)
        .map(order => order.id);
      if (orderIds.length === 0) orderIds = null;
    }
    console.log('[avanzarEmbarqueACargando] PRE updateOrdersStatusByShipment', { id: embarque.id, status: 'ARRIBADO', substatus: 'CARGANDO', orderIds });
    await updateOrdersStatusByShipment(
      embarque.id,
      'ARRIBADO',
      'CARGANDO',
      null,
      orderIds
    );
    console.log('[avanzarEmbarqueACargando] POST updateOrdersStatusByShipment');
  };

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden">      
      {/* Sidebar condicional - en móviles solo mostrar cuando está abierto */}
      <div className={`${!sidebarOpen && 'hidden md:block'} transition-all duration-300 z-40 fixed md:relative`}>
        <Sidebar
          isOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          activeItem={activeItem}
          setActiveItem={setActiveItem}
          onLogout={handleLogout}
        />
      </div>
          {/* Contenido principal - ajustar márgenes para móviles */}
      <div className={`transition-all flex-1 ${sidebarOpen ? 'md:ml-64' : 'ml-0 md:ml-16'} bg-gray-100 min-h-screen w-full`}>
        <Topbar
          title="Confirmar Embarque"
        />
        <main className="p-2 sm:p-4 md:p-6 overflow-y-auto max-h-[calc(100vh-64px)]">
          <div className="bg-white p-3 sm:p-4 md:p-6 rounded-lg shadow mb-4 md:mb-6">
            <div className="flex justify-between items-center mb-4 sticky top-0 bg-white z-10 py-2">
              <div className="flex items-center">
                <button 
                  onClick={() => navigate('/auditorias-calidad')} 
                  className="mr-3 text-gray-600 hover:text-sky-700"
                >
                  <ArrowLeft size={24} />
                </button>
                <h1 className="text-xl md:text-2xl font-semibold text-gray-800">Confirmar Embarque</h1>
              </div>
              
              {/* Botón para mostrar/ocultar sidebar en móviles */}
              <button 
                className="md:hidden bg-sky-600 text-white p-2 rounded-md"
                onClick={() => setSidebarOpen(!sidebarOpen)}
              >
                <span className="sr-only">Menú</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" 
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {sidebarOpen ? <><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></> : 
                  <><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></>}
                </svg>
              </button>
            </div>

            {/* Formulario de búsqueda */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-grow">
                  <label htmlFor="embarqueId" className="block text-sm font-medium text-gray-700 mb-1">
                    ID o Referencia del Embarque
                  </label>
                  <input
                    type="text"
                    id="embarqueId"
                    value={embarqueId}
                    onChange={(e) => setEmbarqueId(e.target.value)}
                    placeholder="Ingresa el ID del embarque"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                  />
                </div>                <div className="self-end">
                  <button
                    onClick={() => {
                      console.log("Botón Buscar presionado, referencia:", embarqueId.trim());
                      buscarEmbarque();
                    }}
                    disabled={loading || !embarqueId.trim()}
                    className={`px-4 py-2 text-white rounded-md transition-colors w-full sm:w-auto ${
                      loading || !embarqueId.trim() ? 'bg-gray-400 cursor-not-allowed' : 'bg-sky-600 hover:bg-sky-700'
                    }`}
                  >
                    {loading ? 'Buscando...' : 'Buscar'}
                  </button>
                </div>
              </div>              {errorMsg && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-red-600 text-sm">{errorMsg}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Verifica que la referencia sea correcta o intenta con otro ID de embarque.
                  </p>
                </div>
              )}
            </div>
            
            {loading && !errorMsg ? (
              <div className="flex justify-center items-center py-10">
                <div className="loader"></div>
                <p className="ml-2">Cargando información del embarque...</p>
              </div>
            ) : (
              <>
                {embarque ? (
                  <div className="space-y-6">
                    {/* Modo Auditoría - Sobreponerse a la interfaz normal */}
                    {modoAuditoria ? (
                      <div className="fixed inset-0 bg-gray-900 bg-opacity-70 z-50 flex flex-col">
                        <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full mx-auto my-auto overflow-y-auto max-h-[90vh] flex flex-col">
                          {/* Header del modal */}
                          <div className="sticky top-0 bg-sky-600 text-white px-4 py-3 flex justify-between items-center rounded-t-lg">
                            <h2 className="font-semibold text-lg">
                              Auditoría de Calidad - Etapa {etapaAuditoria} de 4
                            </h2>
                            <button 
                              onClick={cancelarAuditoria}
                              className="p-1 hover:bg-sky-700 rounded-full focus:outline-none"
                            >
                              <X size={24} />
                            </button>
                          </div>
                          
                          {/* Barra de progreso */}
                          <div className="px-4 pt-3">
                            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-sky-600 transition-all duration-300 ease-in-out rounded-full"
                                style={{ width: `${(etapaAuditoria / 4) * 100}%` }}
                              ></div>
                            </div>
                            <div className="flex justify-between text-xs mt-1 text-gray-500">
                              <span>Fauna</span>
                              <span>Filtraciones</span>
                              <span>Estado Físico</span>
                              <span>Revisión</span>
                            </div>
                          </div>

                          {/* Contenido de la etapa actual */}
                          <div className="flex-1 p-4 overflow-y-auto">
                            {/* Etapa 1: Fauna Nociva */}
                            {etapaAuditoria === 1 && (
                              <div className="space-y-4">
                                <h3 className="text-xl font-medium flex items-center">
                                  <AlertCircle className="mr-2 text-amber-500" size={24} /> 
                                  Evaluación de Fauna Nociva
                                </h3>
                                
                                <p className="text-gray-600">
                                  Verifique si existe presencia de fauna nociva (insectos, roedores, etc.) en el vehículo o embarque.
                                </p>

                                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                  <div className="font-medium mb-2">¿Se detectó fauna nociva?</div>
                                  <div className="flex flex-col sm:flex-row gap-3">
                                    <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                                      <input
                                        type="radio"
                                        name="faunaNociva"
                                        checked={formAuditoria.faunaNociva === true}
                                        onChange={() => setFormAuditoria({...formAuditoria, faunaNociva: true})}
                                        className="h-5 w-5 text-sky-600"
                                      />
                                      <span className="ml-2">Sí, se detectó</span>
                                    </label>
                                    
                                    <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                                      <input
                                        type="radio"
                                        name="faunaNociva"
                                        checked={formAuditoria.faunaNociva === false}
                                        onChange={() => setFormAuditoria({...formAuditoria, faunaNociva: false})}
                                        className="h-5 w-5 text-sky-600"
                                      />
                                      <span className="ml-2">No, está libre de fauna</span>
                                    </label>
                                  </div>
                                </div>

                                {/* Mostrar sección de fotos solo si se detectó fauna */}
                                {formAuditoria.faunaNociva && (
                                  <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                                    <h4 className="font-medium mb-2 flex items-center">
                                      <Camera size={18} className="mr-2 text-sky-600" />
                                      Evidencia Fotográfica
                                    </h4>
                                    
                                    <p className="text-sm text-gray-600 mb-3">
                                      Tome fotos que muestren claramente la fauna nociva detectada
                                    </p>
                                    
                                    {/* Sección para subir fotos */}
                                    <div className="mb-2">
                                      <label 
                                        htmlFor="fotosFaunaNociva" 
                                        className="flex items-center justify-center p-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50"
                                      >
                                        <Camera size={20} className="mr-2 text-sky-600" />
                                        <span>Tomar foto de evidencia</span>
                                        <input
                                          type="file"
                                          id="fotosFaunaNociva"
                                          accept="image/*"
                                          capture="environment"
                                          multiple
                                          onChange={(e) => handleImageUpload(e, 'faunaNociva')}
                                          className="sr-only"
                                        />
                                      </label>
                                    </div>
                                    
                                    {/* Previsualización de fotos */}
                                    {formAuditoria.fotosFaunaNociva.length > 0 && (
                                      <div className="mt-3">
                                        <h5 className="text-sm font-medium mb-2">Fotos capturadas ({formAuditoria.fotosFaunaNociva.length})</h5>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                          {formAuditoria.fotosFaunaNociva.map((foto, index) => (
                                            <div key={index} className="relative">
                                              <img 
                                                src={foto.url} 
                                                alt={`Evidencia ${index + 1}`} 
                                                className="w-full h-24 object-cover rounded-md"
                                              />
                                              <button
                                                onClick={() => eliminarFoto('faunaNociva', index)}
                                                className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1"
                                                title="Eliminar foto"
                                              >
                                                <X size={14} />
                                              </button>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Etapa 2: Filtraciones */}
                            {etapaAuditoria === 2 && (
                              <div className="space-y-4">
                                <h3 className="text-xl font-medium flex items-center">
                                  <Droplet className="mr-2 text-sky-500" size={24} /> 
                                  Evaluación de Filtraciones
                                </h3>
                                
                                <p className="text-gray-600">
                                  Verifique si existen filtraciones de luz o agua en el vehículo que podrían comprometer la carga.
                                </p>

                                <div className="p-3 bg-sky-50 border border-sky-200 rounded-lg">
                                  <div className="font-medium mb-2">¿Se detectaron filtraciones?</div>
                                  <div className="flex flex-col sm:flex-row gap-3">
                                    <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                                      <input
                                        type="radio"
                                        name="filtracionesLuzAgua"
                                        checked={formAuditoria.filtracionesLuzAgua === true}
                                        onChange={() => setFormAuditoria({...formAuditoria, filtracionesLuzAgua: true})}
                                        className="h-5 w-5 text-sky-600"
                                      />
                                      <span className="ml-2">Sí, hay filtraciones</span>
                                    </label>
                                    
                                    <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                                      <input
                                        type="radio"
                                        name="filtracionesLuzAgua"
                                        checked={formAuditoria.filtracionesLuzAgua === false}
                                        onChange={() => setFormAuditoria({...formAuditoria, filtracionesLuzAgua: false})}
                                        className="h-5 w-5 text-sky-600"
                                      />
                                      <span className="ml-2">No, sin filtraciones</span>
                                    </label>
                                  </div>
                                </div>

                                {/* Mostrar sección de fotos solo si se detectaron filtraciones */}
                                {formAuditoria.filtracionesLuzAgua && (
                                  <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                                    <h4 className="font-medium mb-2 flex items-center">
                                      <Camera size={18} className="mr-2 text-sky-600" />
                                      Evidencia Fotográfica
                                    </h4>
                                    
                                    <p className="text-sm text-gray-600 mb-3">
                                      Tome fotos que muestren claramente las filtraciones detectadas
                                    </p>
                                    
                                    {/* Sección para subir fotos */}
                                    <div className="mb-2">
                                      <label 
                                        htmlFor="fotosFiltraciones" 
                                        className="flex items-center justify-center p-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50"
                                      >
                                        <Camera size={20} className="mr-2 text-sky-600" />
                                        <span>Tomar foto de evidencia</span>
                                        <input
                                          type="file"
                                          id="fotosFiltraciones"
                                          accept="image/*"
                                          capture="environment"
                                          multiple
                                          onChange={(e) => handleImageUpload(e, 'filtraciones')}
                                          className="sr-only"
                                        />
                                      </label>
                                    </div>
                                    
                                    {/* Previsualización de fotos */}
                                    {formAuditoria.fotosFiltraciones.length > 0 && (
                                      <div className="mt-3">
                                        <h5 className="text-sm font-medium mb-2">Fotos capturadas ({formAuditoria.fotosFiltraciones.length})</h5>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                          {formAuditoria.fotosFiltraciones.map((foto, index) => (
                                            <div key={index} className="relative">
                                              <img 
                                                src={foto.url} 
                                                alt={`Evidencia ${index + 1}`} 
                                                className="w-full h-24 object-cover rounded-md"
                                              />
                                              <button
                                                onClick={() => eliminarFoto('filtraciones', index)}
                                                className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1"
                                                title="Eliminar foto"
                                              >
                                                <X size={14} />
                                              </button>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Etapa 3: Estado Físico */}
                            {etapaAuditoria === 3 && (
                              <div className="space-y-4">
                                <h3 className="text-xl font-medium flex items-center">
                                  <PanelTop className="mr-2 text-indigo-500" size={24} /> 
                                  Estado Físico
                                </h3>
                                
                                <p className="text-gray-600">
                                  Evalúe el estado físico de paredes y piso del vehículo.
                                </p>

                                <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                                  <div className="font-medium mb-2">¿Cómo calificaría el estado físico?</div>
                                  <div className="flex flex-col gap-2">
                                    <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                                      <input
                                        type="radio"
                                        name="estadoFisico"
                                        value="Bueno"
                                        checked={formAuditoria.estadoFisico === 'Bueno'}
                                        onChange={handleFormChange}
                                        className="h-5 w-5 text-green-600"
                                      />
                                      <span className="ml-2 flex items-center">
                                        <span className="h-3 w-3 rounded-full bg-green-500 inline-block mr-1"></span> 
                                        Bueno - Sin problemas aparentes
                                      </span>
                                    </label>
                                    
                                    <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                                      <input
                                        type="radio"
                                        name="estadoFisico"
                                        value="Regular"
                                        checked={formAuditoria.estadoFisico === 'Regular'}
                                        onChange={handleFormChange}
                                        className="h-5 w-5 text-amber-600"
                                      />
                                      <span className="ml-2 flex items-center">
                                        <span className="h-3 w-3 rounded-full bg-amber-500 inline-block mr-1"></span> 
                                        Regular - Algunos problemas menores
                                      </span>
                                    </label>
                                    
                                    <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                                      <input
                                        type="radio"
                                        name="estadoFisico"
                                        value="Malo"
                                        checked={formAuditoria.estadoFisico === 'Malo'}
                                        onChange={handleFormChange}
                                        className="h-5 w-5 text-red-600"
                                      />
                                      <span className="ml-2 flex items-center">
                                        <span className="h-3 w-3 rounded-full bg-red-500 inline-block mr-1"></span> 
                                        Malo - Problemas importantes
                                      </span>
                                    </label>
                                  </div>
                                </div>

                                {/* Sección de fotos */}
                                <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                                  <h4 className="font-medium mb-2 flex items-center">
                                    <Camera size={18} className="mr-2 text-sky-600" />
                                    Evidencia Fotográfica
                                  </h4>
                                  
                                  <p className="text-sm text-gray-600 mb-3">
                                    Tome fotos que muestren el estado de paredes y piso
                                  </p>
                                  
                                  {/* Sección para subir fotos */}
                                  <div className="mb-2">
                                    <label 
                                      htmlFor="fotosEstadoFisico" 
                                      className="flex items-center justify-center p-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50"
                                    >
                                      <Camera size={20} className="mr-2 text-sky-600" />
                                      <span>Tomar foto de evidencia</span>
                                      <input
                                        type="file"
                                        id="fotosEstadoFisico"
                                        accept="image/*"
                                        capture="environment"
                                        multiple
                                        onChange={(e) => handleImageUpload(e, 'estadoFisico')}
                                        className="sr-only"
                                      />
                                    </label>
                                  </div>
                                  {/* Validación: mostrar advertencia si no hay evidencia */}
                                  {formAuditoria.fotosEstadoFisico.length === 0 && (
                                    <p className="text-xs text-red-600 mt-2">
                                      Es obligatorio adjuntar al menos una foto de evidencia del estado físico para continuar.
                                    </p>
                                  )}
                                  
                                  {/* Previsualización de fotos */}
                                  {formAuditoria.fotosEstadoFisico.length > 0 && (
                                    <div className="mt-3">
                                      <h5 className="text-sm font-medium mb-2">Fotos capturadas ({formAuditoria.fotosEstadoFisico.length})</h5>
                                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                        {formAuditoria.fotosEstadoFisico.map((foto, index) => (
                                          <div key={index} className="relative">
                                            <img 
                                              src={foto.url} 
                                              alt={`Evidencia ${index + 1}`} 
                                              className="w-full h-24 object-cover rounded-md"
                                            />
                                            <button
                                              onClick={() => eliminarFoto('estadoFisico', index)}
                                              className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1"
                                              title="Eliminar foto"
                                            >
                                              <X size={14} />
                                            </button>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            {/* Etapa 4: Comentarios y Decisión Final */}
                            {etapaAuditoria === 4 && (
                              <div className="space-y-4">
                                <h3 className="text-xl font-medium flex items-center">
                                  <CheckCircle className="mr-2 text-green-500" size={24} /> 
                                  Revisión y Decisión Final
                                </h3>
                                
                                <p className="text-gray-600 mb-2">
                                  Resumen de la evaluación:
                                </p>
                                
                                <div className="space-y-3 p-3 bg-gray-50 border border-gray-200 rounded-lg mb-4">
                                  <div className="flex items-center justify-between">
                                    <span className="font-medium">Fauna Nociva:</span>
                                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                      formAuditoria.faunaNociva 
                                        ? 'bg-red-100 text-red-800' 
                                        : 'bg-green-100 text-green-800'
                                    }`}>
                                      {formAuditoria.faunaNociva ? 'Detectada' : 'No Detectada'}
                                    </span>
                                  </div>
                                  
                                  <div className="flex items-center justify-between">
                                    <span className="font-medium">Filtraciones:</span>
                                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                      formAuditoria.filtracionesLuzAgua 
                                        ? 'bg-red-100 text-red-800' 
                                        : 'bg-green-100 text-green-800'
                                    }`}>
                                      {formAuditoria.filtracionesLuzAgua ? 'Detectadas' : 'No Detectadas'}
                                    </span>
                                  </div>
                                  
                                  <div className="flex items-center justify-between">
                                    <span className="font-medium">Estado Físico:</span>
                                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                      formAuditoria.estadoFisico === 'BUENO' ? 'bg-green-100 text-green-800' :
                                      formAuditoria.estadoFisico === 'REGULAR' ? 'bg-amber-100 text-amber-800' :
                                      'bg-red-100 text-red-800'
                                    }`}>
                                      {formAuditoria.estadoFisico}
                                    </span>
                                  </div>
                                  
                                  <div className="pt-2 mt-2 border-t border-gray-200">
                                    <span className="font-medium">Total de evidencias:</span>
                                    <ul className="mt-1 pl-4 text-sm">
                                      <li>Fauna nociva: {formAuditoria.fotosFaunaNociva.length} fotos</li>
                                      <li>Filtraciones: {formAuditoria.fotosFiltraciones.length} fotos</li>
                                      <li>Estado físico: {formAuditoria.fotosEstadoFisico.length} fotos</li>
                                    </ul>
                                  </div>
                                </div>
                                
                                {/* Comentarios */}
                                <div className="space-y-2">
                                  <label htmlFor="comentarios" className="block font-medium text-gray-700">
                                    Comentarios adicionales:
                                  </label>
                                  <textarea
                                    id="comentarios"
                                    name="comentarios"
                                    rows="4"
                                    value={formAuditoria.comentarios}
                                    onChange={handleFormChange}
                                    placeholder="Ingrese aquí cualquier observación o comentario adicional sobre la auditoría..."
                                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                                  ></textarea>
                                </div>
                                
                                {/* Decisión final */}
                                <div className="p-4 bg-gray-100 rounded-lg border border-gray-200">
                                  <h4 className="font-medium mb-3">Decisión Final</h4>
                                  
                                  <div className="flex flex-col sm:flex-row gap-3">
                                    <button
                                      type="button"
                                      onClick={() => setFormAuditoria({...formAuditoria, autorizado: true})}
                                      className={`flex-1 py-3 px-4 rounded-lg flex items-center justify-center ${
                                        formAuditoria.autorizado === true 
                                          ? 'bg-green-600 text-white' 
                                          : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                                      }`}
                                    >
                                      <CheckCircle size={18} className="mr-2" />
                                      Autorizar Embarque
                                    </button>
                                    
                                    <button
                                      type="button"
                                      onClick={() => setFormAuditoria({...formAuditoria, autorizado: false})}
                                      className={`flex-1 py-3 px-4 rounded-lg flex items-center justify-center ${
                                        formAuditoria.autorizado === false 
                                          ? 'bg-red-600 text-white' 
                                          : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                                      }`}
                                    >
                                      <X size={18} className="mr-2" />
                                      Rechazar Embarque
                                    </button>
                                  </div>
                                  
                                  {formAuditoria.autorizado === false && (
                                    <p className="text-sm text-red-500 mt-2">
                                      <AlertCircle size={14} className="inline mr-1" />
                                      El embarque será rechazado por no cumplir con los estándares de calidad.
                                    </p>
                                  )}
                                  
                                  {formAuditoria.autorizado === true && (
                                    <p className="text-sm text-green-600 mt-2">
                                      <CheckCircle size={14} className="inline mr-1" />
                                      El embarque será autorizado para continuar su proceso.
                                    </p>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                          
                          {/* Footer con botones de navegación */}
                          <div className="sticky bottom-0 bg-gray-100 px-4 py-3 rounded-b-lg border-t border-gray-200 flex justify-between">
                            {etapaAuditoria > 1 ? (
                              <button 
                                onClick={retrocederEtapa}
                                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors focus:outline-none"
                              >
                                Anterior
                              </button>
                            ) : (
                              <button 
                                onClick={cancelarAuditoria}
                                className="px-4 py-2 border border-red-200 text-red-600 rounded-md hover:bg-red-50 transition-colors focus:outline-none"
                              >
                                Cancelar
                              </button>
                            )}
                            
                            {etapaAuditoria < 4 ? (
                              <button 
                                onClick={avanzarEtapa}
                                className={`px-4 py-2 bg-sky-600 text-white rounded-md hover:bg-sky-700 transition-colors focus:outline-none ${
                                  etapaAuditoria === 3 && formAuditoria.fotosEstadoFisico.length === 0 ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                                disabled={etapaAuditoria === 3 && formAuditoria.fotosEstadoFisico.length === 0}
                              >
                                Siguiente
                              </button>
                            ) : (                              <button 
                                onClick={enviarAuditoria}
                                disabled={formAuditoria.autorizado === null}
                                className={`px-4 py-2 rounded-md transition-colors focus:outline-none ${
                                  formAuditoria.autorizado === null
                                    ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                                    : 'bg-green-600 text-white hover:bg-green-700'
                                }`}
                              >
                                {loading ? 'Enviando...' : 'Finalizar Auditoría'}
                              </button>
                            )}
                            
                          </div>
                        </div>
                      </div>
                    ) : (                      <>
                        {/* Contenedor con scroll para todo el contenido del embarque */}
                        <div className="overflow-y-auto max-h-[75vh] pr-1 pb-4">
                          {/* Información básica del embarque */}
                          <div className="p-4 border border-sky-100 rounded-lg bg-sky-50 mb-4">
                            <h2 className="text-lg font-semibold text-sky-800 mb-4 flex items-center">
                              <Truck size={20} className="mr-2" /> 
                              Información del Embarque
                            </h2>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                              <div>
                                <p className="text-sm text-gray-500">ID del Embarque</p>
                                <p className="font-medium">{embarque.id}</p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-500">Número de Referencia</p>
                                <p className="font-medium">{embarque.numeroReferencia}</p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-500">Estado</p>
                                <p className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  {embarque.estado}
                                </p>
                              </div>
                            </div>
                          </div>                          {/* Secciones en un grid responsivo para optimizar el espacio */}
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {/* Fechas */}
                            <div className="p-4 border border-gray-200 rounded-lg h-full flex flex-col">
                              <h3 className="text-md font-semibold text-gray-700 mb-3 flex items-center">
                                <CalendarRange size={18} className="mr-2" /> 
                                Fechas
                              </h3>
                              <div className="space-y-3 flex-grow">
                                <div>
                                  <p className="text-sm text-gray-500">Fecha de Salida</p>
                                  <p>{formatearFecha(embarque.fechaSalida)}</p>
                                </div>
                                <div>
                                  <p className="text-sm text-gray-500">Fecha Estimada de Llegada</p>
                                  <p>{formatearFecha(embarque.fechaEstimadaLlegada)}</p>
                                </div>
                                <div>
                                  <p className="text-sm text-gray-500">Última Actualización</p>
                                  <p>{formatearFecha(embarque.lastUpdated)}</p>
                                </div>
                              </div>
                            </div>

                            {/* Origen y Destino */}
                            <div className="p-4 border border-gray-200 rounded-lg h-full flex flex-col">
                              <h3 className="text-md font-semibold text-gray-700 mb-3 flex items-center">
                                <MapPin size={18} className="mr-2" /> 
                                Ubicaciones
                              </h3>
                              <div className="space-y-3 flex-grow">
                                <div>
                                  <p className="text-sm text-gray-500">Origen</p>
                                  <p>{embarque.origen}</p>
                                </div>
                                <div>
                                  <p className="text-sm text-gray-500">Destino</p>
                                  <p>{embarque.destino}</p>
                                </div>
                              </div>
                            </div>

                            {/* Información del Transporte */}
                            <div className="p-4 border border-gray-200 rounded-lg h-full flex flex-col">
                              <h3 className="text-md font-semibold text-gray-700 mb-3 flex items-center">
                                <Truck size={18} className="mr-2" /> 
                                Información del Transporte
                              </h3>
                              <div className="space-y-3 flex-grow">
                                <div>
                                  <p className="text-sm text-gray-500">Transportista</p>
                                  <p>{embarque.transportista}</p>
                                </div>
                                <div>
                                  <p className="text-sm text-gray-500">Conductor</p>
                                  <p>{embarque.conductor}</p>
                                </div>
                                <div>
                                  <p className="text-sm text-gray-500">Vehículo</p>
                                  <p>{embarque.vehiculo}</p>
                                </div>
                              </div>
                            </div>                            {/* Cliente y Carga - Una sola columna igual que las demás tarjetas */}
                            <div className="p-4 border border-gray-200 rounded-lg h-full flex flex-col">
                              <h3 className="text-md font-semibold text-gray-700 mb-3 flex items-center">
                                <User size={18} className="mr-2" /> 
                                Cliente y Carga
                              </h3>
                              <div className="flex-grow">
                                <div>
                                  <p className="text-sm text-gray-500">Cliente</p>
                                  <p className="mb-3">{embarque.cliente}</p>
                                </div>
                                <div>
                                  <p className="text-sm text-gray-500 mb-1">Carga</p>
                                  <ul className="list-disc list-inside space-y-1 text-sm max-h-[200px] overflow-y-auto border border-gray-100 rounded p-2">
                                    {embarque.carga.map((item, index) => (
                                      <li key={index}>
                                        {item.descripcion} - {item.cantidad} {item.unidad}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                            </div>
                            
                            {/* Auditorías de Calidad - Ocupa todo el ancho en pantallas medianas y grandes por la extensión potencial de su contenido */}
                            <div className="p-4 border border-gray-200 rounded-lg h-full flex flex-col md:col-span-full">
                              <h3 className="text-md font-semibold text-gray-700 mb-3 flex items-center">
                                <CheckCircle size={18} className="mr-2" /> 
                                Auditorías de Calidad
                              </h3>
                              {auditorias && auditorias.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[300px] overflow-y-auto pr-1">
                                  {auditorias.map((auditoria, index) => (
                                    <div key={index} className={`p-3 rounded-md h-full ${
                                      auditoria.estatus === 'AUTORIZADO' 
                                        ? 'bg-green-50 border border-green-200' 
                                        : 'bg-red-50 border border-red-200'
                                    }`}>
                                      <div className="flex justify-between items-center mb-1">
                                        <span className="font-medium">Auditoría </span>
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                          auditoria.estatus === 'AUTORIZADO' 
                                            ? 'bg-green-100 text-green-800' 
                                            : 'bg-red-100 text-red-800'
                                        }`}>
                                          {auditoria.estatus}
                                        </span>
                                      </div>
                                      <div className="flex justify-between items-center">                                        <p className="text-sm">
                                          <span className="font-medium">Fecha:</span> {formatearFecha(auditoria.created_at)}
                                        </p>
                                        <AuditoriaPDF auditoria={auditoria} />
                                      </div>
                                      {auditoria.comentarios && (
                                        <p className="text-sm mt-1">
                                          <span className="font-medium">Comentarios:</span> {auditoria.comentarios}
                                        </p>
                                      )}
                                      <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                                        <div className={`p-1 rounded ${
                                          auditoria.fauna_nociva ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                                        }`}>
                                          <span className="block font-medium">Fauna nociva:</span>
                                          {auditoria.fauna_nociva ? 'Detectada' : 'No detectada'}
                                        </div>
                                        <div className={`p-1 rounded ${
                                          auditoria.filtracion ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                                        }`}>
                                          <span className="block font-medium">Filtraciones:</span>
                                          {auditoria.filtracion ? 'Detectadas' : 'No detectadas'}
                                        </div>
                                        <div className={`p-1 rounded ${
                                          auditoria.estado_fisico === 'BUENO' ? 'bg-green-100 text-green-800' :
                                          auditoria.estado_fisico === 'REGULAR' ? 'bg-amber-100 text-amber-800' :
                                          'bg-red-100 text-red-800'
                                        }`}>
                                          <span className="block font-medium">Estado físico:</span>
                                          {auditoria.estado_fisico}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div>
                                  
                                  <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">No hay auditorías de calidad registradas para este embarque.
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Botones de acción - Fijos al final */}
                        <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4 mt-4 sticky bottom-0 bg-white pb-2">
                          <button 
                            className="px-4 py-3 bg-sky-600 text-white rounded-md hover:bg-sky-700 transition-colors flex items-center justify-center"
                            onClick={iniciarAuditoria}
                          >
                            <CheckCircle size={18} className="mr-2" />
                            <span>Iniciar Auditoría de Calidad</span>
                          </button>
                          <button 
                            className="px-4 py-3 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors flex items-center justify-center"
                            onClick={() => navigate('/auditorias-calidad')}
                          >
                            <ArrowLeft size={18} className="mr-2" />
                            <span>Volver</span>
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  !errorMsg && (
                    <div className="text-center py-10">
                      <FileText size={48} className="mx-auto text-gray-400 mb-3" />
                      <p className="text-gray-500">Ingresa un ID de embarque para ver su información</p>
                    </div>
                  )
                )}
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ConfirmarEmbarque;
